/* eslint-disable @typescript-eslint/no-explicit-any -- Detailed view payload requires explicit any */
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
} from '../../../components/ui/Drawer';
import { Button } from '../../../components/ui/Button';
import { Money } from '../../../components/ui/Money';
import { useSubscriberDetailed } from '../network';

interface MemberDetailDrawerProps {
  schemeId: string;
  subscriberId: string | null;
  onClose: () => void;
  // Edit/delete member is not yet implemented (no backend endpoint) -- omit these props to hide
  // the buttons rather than wiring them to a no-op; a clickable no-op is worse than no affordance.
  onEdit?: () => void;
  onDelete?: () => void;
}

export function MemberDetailDrawer({
  schemeId,
  subscriberId,
  onClose,
  onEdit,
  onDelete,
}: MemberDetailDrawerProps) {
  const { data: detailData, isLoading, error } = useSubscriberDetailed(schemeId, subscriberId);

  const detail = detailData as any;
  const subscriber = detail?.subscriber;
  const customer = detail?.customer;
  const scheme = detail?.scheme;
  const addresses = (detail?.addresses || []) as any[];
  const nominees = (detail?.nominees || []) as any[];
  const canvasAgent = detail?.canvasAgent as { fullName: string } | null;
  const collectionAgent = detail?.collectionAgent as { fullName: string } | null;

  return (
    <Drawer open={!!subscriberId} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="w-full max-w-lg bg-surface-1">
        <DrawerHeader>
          <DrawerTitle>Subscriber Member Details</DrawerTitle>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto space-y-6">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div className="w-8 h-8 border-4 border-accent-200 border-t-accent-600 rounded-full animate-spin" />
              <p className="text-sm text-text-muted">Loading subscriber details...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-danger-bg border border-danger-500 rounded-[6px] text-sm text-danger-text">
              Failed to load subscriber details: {(error as Error).message || 'Unknown error'}
            </div>
          )}

          {!isLoading && !error && detailData && (
            <div className="space-y-6">
              {/* Customer Information */}
              <div className="bg-surface-2 p-4 border border-border rounded-[6px] space-y-3">
                <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Customer Info</h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div>
                    <span className="text-text-muted block text-xs">Full Name</span>
                    <span className="font-semibold text-text-primary">{customer?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Date of Birth</span>
                    <span className="font-medium text-text-primary">{customer?.dob || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Subscription Information */}
              <div className="bg-surface-2 p-4 border border-border rounded-[6px] space-y-3">
                <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Subscription Info</h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div>
                    <span className="text-text-muted block text-xs">Ticket Number(s)</span>
                    <span className="font-semibold text-text-primary">
                      {subscriber?.ticketNumbers ? subscriber.ticketNumbers.join(', ') : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Status</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-[4px] text-xs font-semibold bg-accent-100 text-accent-800">
                      {subscriber?.status || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Enrolled At</span>
                    <span className="font-medium text-text-primary">
                      {subscriber?.enrolledAt ? new Date(subscriber.enrolledAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Running Balance</span>
                    <span className="font-medium text-text-primary">
                      {subscriber?.runningBalancePaise !== undefined && subscriber?.runningBalancePaise !== null
                        ? <Money amount={subscriber.runningBalancePaise} />
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Scheme Information */}
              <div className="bg-surface-2 p-4 border border-border rounded-[6px] space-y-3">
                <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Scheme Info</h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div>
                    <span className="text-text-muted block text-xs">Scheme Name</span>
                    <span className="font-semibold text-text-primary">{scheme?.displayName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Scheme Number</span>
                    <span className="font-semibold text-text-primary">{scheme?.schemeNumber || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Chit Value</span>
                    <span className="font-medium text-text-primary">
                      {scheme?.chitValue !== undefined && scheme?.chitValue !== null ? <Money amount={scheme.chitValue} /> : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-text-muted block text-xs">Duration</span>
                    <span className="font-medium text-text-primary">
                      {scheme?.durationCycles ? `${scheme.durationCycles} Cycles` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Agents */}
              {(canvasAgent || collectionAgent) && (
                <div className="bg-surface-2 p-4 border border-border rounded-[6px] space-y-3">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Agents</h3>
                  <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                    {canvasAgent && (
                      <div>
                        <span className="text-text-muted block text-xs">Canvas Agent</span>
                        <span className="font-medium text-text-primary">{canvasAgent.fullName}</span>
                      </div>
                    )}
                    {collectionAgent && (
                      <div>
                        <span className="text-text-muted block text-xs">Collection Agent</span>
                        <span className="font-medium text-text-primary">{collectionAgent.fullName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Nominees */}
              {nominees.length > 0 && (
                <div className="bg-surface-2 p-4 border border-border rounded-[6px] space-y-3">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Nominees</h3>
                  <div className="space-y-2">
                    {nominees.map((nominee: any, idx: number) => (
                      <div key={idx} className="flex justify-between text-sm py-1 border-b border-border last:border-0">
                        <span className="font-medium text-text-primary">{nominee.name}</span>
                        <span className="text-text-muted">{nominee.relation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Addresses */}
              {addresses.length > 0 && (
                <div className="bg-surface-2 p-4 border border-border rounded-[6px] space-y-3">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Addresses</h3>
                  <div className="space-y-3">
                    {addresses.map((addr: any, idx: number) => (
                      <div key={idx} className="text-sm py-1 border-b border-border last:border-0">
                        <span className="text-text-muted block text-xs uppercase font-bold">{addr.addressType}</span>
                        <span className="text-text-primary">
                          {addr.addressLine1}
                          {addr.addressLine2 ? `, ${addr.addressLine2}` : ''}
                          , {addr.city}, {addr.stateCode} - {addr.pincode}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DrawerBody>

        <DrawerFooter className="border-t border-border p-4 bg-surface-2 flex gap-3">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Close
          </Button>
          {onEdit && (
            <Button variant="secondary" onClick={onEdit} className="flex-1">
              Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="danger" onClick={onDelete} className="flex-1 bg-danger-500 hover:bg-danger-600 text-white">
              Delete
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
